import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import Button from '../components/ui/Button';
import { useCheckout } from '../context/CheckoutContext';
import { formatIdrFromUsd } from '../utils/currency';

const ShippingAddress = () => {
  const navigate = useNavigate();
  const { checkoutData, updateCheckoutData, hasRequiredDataForStep } = useCheckout();
  const { productId, productName, selectedSize, price, shippingAddress } = checkoutData;
  
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    postalCode: '',
    country: ''
  });

  // Check if we have the required data for this step
  useEffect(() => {
    if (!hasRequiredDataForStep('shipping')) {
      navigate('/products');
    }
  }, [hasRequiredDataForStep, navigate]);

  // Pre-fill form data if we have shipping address
  useEffect(() => {
    if (shippingAddress) {
      setFormData(shippingAddress);
    }
  }, [shippingAddress]);
  
  const [errors, setErrors] = useState({});
  
  // Handle form input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when field is edited
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };
  
  // Validate form
  const validateForm = () => {
    const newErrors = {};
    
    // Required fields
    const requiredFields = ['fullName', 'email', 'phone', 'addressLine1', 'city', 'state', 'postalCode', 'country'];
    requiredFields.forEach(field => {
      if (!formData[field].trim()) {
        newErrors[field] = 'This field is required';
      }
    });
    
    // Email validation
    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      // Update checkout data with shipping address
      updateCheckoutData({
        shippingAddress: formData
      });
      
      // Proceed to shipping options
      navigate('/checkout/shipping-options');
    }
  };
  
  // Check if we have the necessary data
  if (!productId || !price) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-16 text-center">
          <h1 className="font-serif text-3xl font-bold text-primary mb-6">Missing Order Information</h1>
          <p className="text-light mb-8">Please start your order from the product page.</p>
          <Button onClick={() => navigate('/products')} variant="primary">
            Browse Products
          </Button>
        </div>
      </Layout>
    );
  }
  
  return (
    <Layout>
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-3xl mx-auto">
          {/* Checkout Steps */}
          <div className="mb-8">
            <div className="flex justify-between items-center">
              <div className="flex flex-col items-center">
                <div className="w-10 h-10 bg-gray-700 text-gray-400 rounded-full flex items-center justify-center font-bold">1</div>
                <span className="text-gray-400 text-sm mt-2">Confirmation</span>
              </div>
              <div className="flex-1 h-1 bg-gray-700 mx-2"></div>
              <div className="flex flex-col items-center">
                <div className="w-10 h-10 bg-primary text-dark rounded-full flex items-center justify-center font-bold">2</div>
                <span className="text-primary text-sm mt-2">Shipping</span>
              </div>
              <div className="flex-1 h-1 bg-gray-700 mx-2"></div>
              <div className="flex flex-col items-center">
                <div className="w-10 h-10 bg-gray-700 text-gray-400 rounded-full flex items-center justify-center font-bold">3</div>
                <span className="text-gray-400 text-sm mt-2">Payment</span>
              </div>
            </div>
          </div>
          
          <h1 className="font-serif text-3xl font-bold text-primary mb-6">
            Shipping Address
          </h1>
          
          <div className="bg-secondary border border-gray-800 rounded-lg overflow-hidden mb-8">
            <div className="p-6">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="fullName" className="block text-light mb-2">
                      Full Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="fullName"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleChange}
                      className={`w-full px-4 py-2 bg-dark border ${errors.fullName ? 'border-red-500' : 'border-gray-700'} rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50 text-light`}
                    />
                    {errors.fullName && (
                      <p className="text-red-500 text-sm mt-1">{errors.fullName}</p>
                    )}
                  </div>
                  
                  <div>
                    <label htmlFor="email" className="block text-light mb-2">
                      Email <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className={`w-full px-4 py-2 bg-dark border ${errors.email ? 'border-red-500' : 'border-gray-700'} rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50 text-light`}
                    />
                    {errors.email && (
                      <p className="text-red-500 text-sm mt-1">{errors.email}</p>
                    )}
                  </div>
                </div>
                
                <div>
                  <label htmlFor="phone" className="block text-light mb-2">
                    Phone Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 bg-dark border ${errors.phone ? 'border-red-500' : 'border-gray-700'} rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50 text-light`}
                  />
                  {errors.phone && (
                    <p className="text-red-500 text-sm mt-1">{errors.phone}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="addressLine1" className="block text-light mb-2">
                    Address Line 1 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="addressLine1"
                    name="addressLine1"
                    value={formData.addressLine1}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 bg-dark border ${errors.addressLine1 ? 'border-red-500' : 'border-gray-700'} rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50 text-light`}
                  />
                  {errors.addressLine1 && (
                    <p className="text-red-500 text-sm mt-1">{errors.addressLine1}</p>
                  )}
                </div>
                
                <div>
                  <label htmlFor="addressLine2" className="block text-light mb-2">
                    Address Line 2 (Optional)
                  </label>
                  <input
                    type="text"
                    id="addressLine2"
                    name="addressLine2"
                    value={formData.addressLine2}
                    onChange={handleChange}
                    className="w-full px-4 py-2 bg-dark border border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50 text-light"
                  />
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="city" className="block text-light mb-2">
                      City <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="city"
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      className={`w-full px-4 py-2 bg-dark border ${errors.city ? 'border-red-500' : 'border-gray-700'} rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50 text-light`}
                    />
                    {errors.city && (
                      <p className="text-red-500 text-sm mt-1">{errors.city}</p>
                    )}
                  </div>
                  
                  <div>
                    <label htmlFor="state" className="block text-light mb-2">
                      State/Province <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="state"
                      name="state"
                      value={formData.state}
                      onChange={handleChange}
                      className={`w-full px-4 py-2 bg-dark border ${errors.state ? 'border-red-500' : 'border-gray-700'} rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50 text-light`}
                    />
                    {errors.state && (
                      <p className="text-red-500 text-sm mt-1">{errors.state}</p>
                    )}
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="postalCode" className="block text-light mb-2">
                      Postal/ZIP Code <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="postalCode"
                      name="postalCode"
                      value={formData.postalCode}
                      onChange={handleChange}
                      className={`w-full px-4 py-2 bg-dark border ${errors.postalCode ? 'border-red-500' : 'border-gray-700'} rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50 text-light`}
                    />
                    {errors.postalCode && (
                      <p className="text-red-500 text-sm mt-1">{errors.postalCode}</p>
                    )}
                  </div>
                  
                  <div>
                    <label htmlFor="country" className="block text-light mb-2">
                      Country <span className="text-red-500">*</span>
                    </label>
                    <select
                      id="country"
                      name="country"
                      value={formData.country}
                      onChange={handleChange}
                      className={`w-full px-4 py-2 bg-dark border ${errors.country ? 'border-red-500' : 'border-gray-700'} rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50 text-light`}
                    >
                      <option value="">Select Country</option>
                      <option value="Australia">Australia</option>
                      <option value="Canada">Canada</option>
                      <option value="China">China</option>
                      <option value="France">France</option>
                      <option value="Germany">Germany</option>
                      <option value="India">India</option>
                      <option value="Indonesia">Indonesia</option>
                      <option value="Italy">Italy</option>
                      <option value="Japan">Japan</option>
                      <option value="Malaysia">Malaysia</option>
                      <option value="Netherlands">Netherlands</option>
                      <option value="New Zealand">New Zealand</option>
                      <option value="Singapore">Singapore</option>
                      <option value="South Korea">South Korea</option>
                      <option value="Spain">Spain</option>
                      <option value="Thailand">Thailand</option>
                      <option value="United Kingdom">United Kingdom</option>
                      <option value="United States">United States</option>
                      <option value="Vietnam">Vietnam</option>
                    </select>
                    {errors.country && (
                      <p className="text-red-500 text-sm mt-1">{errors.country}</p>
                    )}
                  </div>
                </div>
                
                <div className="pt-4 flex justify-between">
                  <Button 
                    type="button" 
                    variant="secondary" 
                    onClick={() => navigate(-1)}
                  >
                    Back
                  </Button>
                  <Button 
                    type="submit" 
                    variant="primary"
                  >
                    Continue to Shipping Options
                  </Button>
                </div>
              </form>
            </div>
          </div>
          
          <div className="bg-secondary border border-gray-800 rounded-lg overflow-hidden">
            <div className="p-6">
              <h2 className="font-serif text-xl font-bold text-primary mb-4">
                Order Summary
              </h2>
              <div className="flex justify-between mb-2">
                <span className="text-gray-400">Product</span>
                <span className="text-light">{productName}</span>
              </div>
              {selectedSize && (
                <div className="flex justify-between mb-2">
                  <span className="text-gray-400">Size</span>
                  <span className="text-light">{selectedSize}</span>
                </div>
              )}
              <div className="flex justify-between mb-2">
                <span className="text-gray-400">Subtotal</span>
                <span className="text-light">{formatIdrFromUsd(price)}</span>
              </div>
              <div className="flex justify-between mb-2">
                <span className="text-gray-400">Shipping</span>
                <span className="text-light">Calculated at next step</span>
              </div>
              <div className="flex justify-between font-bold text-lg pt-2 border-t border-gray-800 mt-2">
                <span className="text-light">Total</span>
                <span className="text-primary">{formatIdrFromUsd(price)} + Shipping</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ShippingAddress;
